import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { memo, useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { useIsDesktopDimension } from "../hooks/useGetDimensionGroup";
import { useOnClickOutside } from "../hooks/useOnClickOutSide";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { Portal } from "../Portal";
import { Button } from "../Button";
import { CloseIcon } from "../icons";
import { Preloader } from "../Preloader";
import style from "./styles.pcss";
import { HeaderBar } from "./components/HeaderBar";
import { ActionBar } from "./components/ActionBar";
var cn = classNames.bind(style);

/* вынести оверлэй как отдельный компонент */
export var Modal = /*#__PURE__*/memo(function (_ref) {
  var _cn, _cn2, _cn3, _cn4;
  var isOpen = _ref.isOpen,
    children = _ref.children,
    onClose = _ref.onClose,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'l' : _ref$size,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? 'light' : _ref$theme,
    _ref$isNoPadding = _ref.isNoPadding,
    isNoPadding = _ref$isNoPadding === void 0 ? false : _ref$isNoPadding,
    isNoFixHeight = _ref.isNoFixHeight,
    _ref$isCloseButtonVis = _ref.isCloseButtonVisible,
    isCloseButtonVisible = _ref$isCloseButtonVis === void 0 ? true : _ref$isCloseButtonVis,
    _ref$testName = _ref.testName,
    testName = _ref$testName === void 0 ? 'Modal' : _ref$testName,
    actionBarComponent = _ref.actionBar,
    _ref$headerBarConfig = _ref.headerBarConfig,
    _ref$headerBarConfig2 = _ref$headerBarConfig === void 0 ? {} : _ref$headerBarConfig,
    backButtonText = _ref$headerBarConfig2.backButtonText,
    backButtonClick = _ref$headerBarConfig2.backButtonClick,
    headerText = _ref$headerBarConfig2.headerText,
    backButtonHref = _ref$headerBarConfig2.backButtonHref,
    subtitleText = _ref$headerBarConfig2.subtitleText,
    isLoading = _ref.isLoading,
    isFixedMinValues = _ref.isFixedMinValues,
    isBodyScrollHiddenOnMobile = _ref.isBodyScrollHiddenOnMobile,
    _ref$isFitMobile = _ref.isFitMobile,
    isFitMobile = _ref$isFitMobile === void 0 ? true : _ref$isFitMobile;
  var isDesktop = useIsDesktopDimension();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isIos = _useState2[0],
    setIsIos = _useState2[1];
  var commonModalRef = useRef(null);
  var overlayRef = useRef(null);
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    browserNavbarHeight = _useState4[0],
    setBrowserNavbarHeight = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    bodyScrollHeight = _useState6[0],
    setbodyScrollHeight = _useState6[1];
  useOnClickOutside({
    ref: commonModalRef,
    handler: onClose,
    isOpen: isOpen,
    overlayRef: overlayRef
  });
  var focusTrapElement = useFocusTrap({
    isActive: isOpen,
    containerRef: overlayRef
  });
  useEffect(function () {
    setIsIos(navigator.userAgent.includes('Mac') && 'ontouchend' in document);
  }, []);
  useEffect(function () {
    var keyHandler = function keyHandler(_ref2) {
      var code = _ref2.code;
      if (code === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keyup', keyHandler);
    return function () {
      window.removeEventListener('keyup', keyHandler);
    };
  }, [onClose, isOpen]);
  useEffect(function () {
    /** расчеты для того, чтобы при открытии и закрытии модалки
     * контент не прыгал вверх страницы и учет навбара сафари (особенности иоса) */
    if (isIos) {
      var handleMove = function handleMove() {
        /** вычисляем высоту панели навигации сафари, чтобы учитывать ее при отработке промотки
         * для фиксирования основного контента под модалкой в прежнем положении */
        var navbarHeight = window.innerHeight - document.documentElement.clientHeight;
        setBrowserNavbarHeight(function (prevState) {
          if (prevState < navbarHeight) {
            return navbarHeight;
          }
          return prevState;
        });
        var scrollingElement = document.scrollingElement || document.documentElement;
        var bodyScrollTop = scrollingElement.scrollTop;
        /** вычисляем на какую высоту был прокручен контент, чтобы корректно фиксировать
         * основной контент под модалкой в прежнем положении */
        setbodyScrollHeight(bodyScrollTop);
      };
      document.addEventListener('touchmove', handleMove);
      return function () {
        document.removeEventListener('touchmove', handleMove);
      };
    }
    return undefined;
  }, [isIos]);
  useEffect(function () {
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    var lastTouchY;
    var lastTouchX;
    var bodyChildren;
    var scrollbarHeight = window.innerHeight - document.documentElement.clientHeight;
    if (isIos) {
      /**
       * должен быть использован querySelector `body > *`,
       * так как event.preventDefault на body или html element ничего не делает
       */
      bodyChildren = Array.from(document.querySelectorAll('body > *'));
    }
    var preventRubberBand = function preventRubberBand(event) {
      var allowScroll = false;
      if (event.type === 'touchstart' || event.cancelable === false) {
        lastTouchY = event.touches[0].clientY;
        lastTouchX = event.touches[0].clientX;
      } else {
        var closestScrollable = event.target;
        var currentY = event.touches[0].clientY;
        var currentX = event.touches[0].clientX;
        var deltaY = lastTouchY - currentY;
        var deltaX = lastTouchX - currentX;

        /**
         * запуск цикла по родителям элемента от event.target до document.body
         */
        while (closestScrollable && closestScrollable.tagName !== 'BODY') {
          var _closestScrollable = closestScrollable,
            scrollTop = _closestScrollable.scrollTop,
            clientHeight = _closestScrollable.clientHeight,
            scrollHeight = _closestScrollable.scrollHeight;
          lastTouchY = currentY;

          /**
           * если направление скроллинга идет вверх и скроллбар нашего основго элемента не вверху
           * или направление скроллинга идет вниз и скроллбар нашего основго элемента не внизу
           */
          if (deltaY < 0 && scrollTop > 0 || deltaY > 0 && scrollHeight - clientHeight > scrollTop || deltaX !== 0 &&
          /**
           * когда сафари показывает верхний и нижний навбар высота элемента обрезается на высоту
           * навбара без добавления скролла, поэтому мы игнорируем элемент с тем же scrollHeight как у body
           */
          scrollHeight !== document.body.clientHeight) {
            /**
             * нашли скролящийся элемент, все в порядке, скролл разрешен
             */
            allowScroll = true;
            break;
          }
          closestScrollable = closestScrollable.parentNode;
        }

        /**
         * если все предки таргета проверены на всем пути до body и ни один из них не имеет доступного скролла,
         * то резиновое поведение скроллинга иоса сафари надо остановить (rubber band scroll)
         */
        if (!allowScroll) {
          event.preventDefault();
        }
      }
    };
    if (isOpen) {
      if (!isIos) {
        document.documentElement.classList.add(cn('scroll-hidden'));
        document.body.style.paddingRight = "".concat(scrollbarWidth, "px");
        document.body.style.overflowX = 'visible';
        if (isBodyScrollHiddenOnMobile) {
          document.body.style.overflow = 'hidden';
        }
      }
      if (isIos) {
        document.documentElement.classList.add(cn('fix-ios-scroll'));
        /** когда навбар виден, разница составаляет 0, поэтому
         * чтобы при открытом навбаре контент под модалкой не сдвинулся,
         * надо заранее знать высоту этого навбара и сдвигать контент с ее учетом */
        if (scrollbarHeight === 0) {
          document.body.scrollTop = bodyScrollHeight - browserNavbarHeight;
        } else {
          document.body.scrollTop = bodyScrollHeight;
        }
        if (isBodyScrollHiddenOnMobile) {
          document.body.style.overflow = 'hidden';
        }
        /** чтобы не скролился контент под модалкой, но при
         * этом контент в ней самой оставался бы прокручиваемым (особенности работы body в safari ios) */
        document.addEventListener('touchstart', preventRubberBand);
        bodyChildren.forEach(function (item) {
          return item.addEventListener('touchmove', preventRubberBand);
        });
      }
    }
    return function () {
      document.documentElement.classList.remove(cn('scroll-hidden'));
      document.body.style.overflow = 'auto';
      document.body.style.overflowX = 'hidden';
      document.documentElement.classList.remove(cn('fix-ios-scroll'));
      if (isIos) {
        document.removeEventListener('touchstart', preventRubberBand);
        bodyChildren.forEach(function (item) {
          return item.removeEventListener('touchmove', preventRubberBand);
        });
      }
      if (!isIos) {
        document.body.style.paddingRight = '0';
      }
    };
  }, [isOpen, browserNavbarHeight, bodyScrollHeight, isIos]);
  return isOpen ? /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement("div", {
    "aria-modal": "true",
    className: cn('overlay', (_cn = {}, _defineProperty(_cn, "overlay--".concat(size), !!size), _defineProperty(_cn, "overlay--".concat(theme), !!theme), _defineProperty(_cn, 'overlay--active', isOpen), _defineProperty(_cn, 'overlay--no-padding', isNoPadding), _defineProperty(_cn, 'overlay--fit-content', isFitMobile), _cn)),
    ref: overlayRef,
    role: "dialog"
  }, /*#__PURE__*/React.createElement("div", {
    className: cn('modal-wrapper', (_cn2 = {}, _defineProperty(_cn2, "modal-wrapper--".concat(size), !!size), _defineProperty(_cn2, 'modal-wrapper--no-padding', isNoPadding && !isFixedMinValues), _cn2))
  }, /*#__PURE__*/React.createElement("div", {
    className: cn('modal', "modal--".concat(size), "modal--".concat(theme), (_cn3 = {
      'modal--no-padding': isNoPadding && !isFixedMinValues,
      'modal--no-padding-fixed': isNoPadding && isFixedMinValues
    }, _defineProperty(_cn3, "modal--".concat(size, "-closing-icon"), isCloseButtonVisible), _defineProperty(_cn3, 'modal--short', isNoFixHeight), _cn3)),
    "data-component": testName,
    ref: commonModalRef
  }, /*#__PURE__*/React.createElement(HeaderBar, {
    backButtonClick: backButtonClick,
    backButtonHref: backButtonHref,
    backButtonText: backButtonText,
    headerText: headerText,
    isCloseButtonVisible: isCloseButtonVisible,
    isLoading: isLoading,
    isNoPadding: isNoPadding,
    size: size,
    subtitleText: subtitleText
  }), isCloseButtonVisible && /*#__PURE__*/React.createElement("div", {
    className: cn('modal__button-wrapper')
  }, /*#__PURE__*/React.createElement(Button, {
    icon: /*#__PURE__*/React.createElement(CloseIcon, {
      fill: theme === 'dark' ? '#fdfdfe' : '#5a5e62',
      size: isDesktop ? 32 : 24
    }),
    onClick: onClose,
    variant: "plain"
  })), /*#__PURE__*/React.createElement("div", {
    className: cn((_cn4 = {}, _defineProperty(_cn4, "modal__content--".concat(size), !!size), _defineProperty(_cn4, 'modal__content--loading', isLoading), _defineProperty(_cn4, 'modal__content--no-padding', isNoPadding && !isFixedMinValues), _defineProperty(_cn4, 'modal__content--plain', isNoFixHeight), _cn4))
  }, isLoading ? /*#__PURE__*/React.createElement(Preloader, {
    color: "contentPlaceholder",
    size: "l"
  }) : children), actionBarComponent || /*#__PURE__*/React.createElement(ActionBar, {
    isLoading: isLoading,
    isNoFixHeight: isNoFixHeight,
    isNoPadding: isNoPadding,
    size: size
  }))), focusTrapElement)) : null;
});
Modal.displayName = Modal.name;